/**
 * UserAssignmentManager Component
 * Allows Admin/Executive to manage DeptMgr location and department assignments
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  UserCircleIcon,
  MapPinIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { getAllUsers, saveUserAssignment, UserAssignment } from '../../services/usersApi';
import { LOCATIONS } from '../LocationDropdown';
import { useAuth } from '../../auth/AuthContext';

/** Assignable departments with display labels */
const ASSIGNABLE_DEPARTMENTS = [
  { key: 'NEW_VEHICLE', label: 'New' },
  { key: 'USED_VEHICLE', label: 'Used' },
  { key: 'SERVICE', label: 'Service' },
  { key: 'PARTS', label: 'Parts' },
  { key: 'BODY_SHOP', label: 'Body' },
  { key: 'RENTAL', label: 'Rental' },
  { key: 'OTHERS', label: 'Others' },
] as const;

interface EditingUser {
  userId: string;
  locationIds: number[];
  departmentIds: string[];
}

type SortField = 'displayName' | 'role' | 'assignedLocations';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 10;

export const UserAssignmentManager: React.FC = () => {
  const { isAdmin, isExecutive } = useAuth();
  const [users, setUsers] = useState<UserAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<EditingUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterRole, setFilterRole] = useState<string>('all');

  // Sort state
  const [sortField, setSortField] = useState<SortField>('displayName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  const canEdit = isAdmin || isExecutive;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        setError(response.error || 'Failed to load users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const startEditing = (user: UserAssignment) => {
    setEditingUser({
      userId: user.userId,
      locationIds: [...(user.assignedLocationIds || [])],
      departmentIds: [...(user.assignedDepartmentIds || [])],
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };

  const toggleLocation = (locationId: number) => {
    if (!editingUser) return;

    setEditingUser(prev => {
      if (!prev) return null;
      const hasLocation = prev.locationIds.includes(locationId);
      return {
        ...prev,
        locationIds: hasLocation
          ? prev.locationIds.filter(id => id !== locationId)
          : [...prev.locationIds, locationId],
      };
    });
  };

  const toggleDepartment = (departmentId: string) => {
    if (!editingUser) return;

    setEditingUser(prev => {
      if (!prev) return null;
      const hasDept = prev.departmentIds.includes(departmentId);
      return {
        ...prev,
        departmentIds: hasDept
          ? prev.departmentIds.filter(id => id !== departmentId)
          : [...prev.departmentIds, departmentId],
      };
    });
  };

  const saveAssignment = async () => {
    if (!editingUser) return;

    const user = users.find(u => u.userId === editingUser.userId);
    if (!user) return;

    setSaving(true);
    try {
      const response = await saveUserAssignment({
        userId: user.userId,
        email: user.email,
        displayName: user.displayName,
        role: user.role,
        assignedLocationIds: editingUser.locationIds,
        assignedDepartmentIds: editingUser.departmentIds,
      });

      if (response.success) {
        // Update local state
        setUsers(prev => prev.map(u =>
          u.userId === user.userId
            ? { ...u, assignedLocationIds: editingUser.locationIds, assignedDepartmentIds: editingUser.departmentIds }
            : u
        ));
        setEditingUser(null);
      } else {
        setError(response.error || 'Failed to save assignment');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const getLocationName = (locationId: number): string => {
    const location = LOCATIONS.find(l => l.id === locationId);
    return location?.name || `Location ${locationId}`;
  };

  const getRoleBadgeClass = (role: string): string => {
    switch (role) {
      case 'Admin':
        return 'bg-red-100 text-red-800';
      case 'CEO':
        return 'bg-purple-200 text-purple-900';
      case 'Executive':
        return 'bg-purple-100 text-purple-800';
      case 'GeneralManager':
        return 'bg-blue-100 text-blue-800';
      case 'DepartmentManager':
        return 'bg-green-100 text-green-800';
      case 'FinanceManager':
        return 'bg-cyan-100 text-cyan-800';
      case 'Insurance':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredUsers = filterRole === 'all'
    ? users
    : users.filter(u => u.role === filterRole);

  // Sort users
  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'displayName':
          comparison = (a.displayName || '').localeCompare(b.displayName || '');
          break;
        case 'role':
          comparison = (a.role || '').localeCompare(b.role || '');
          break;
        case 'assignedLocations':
          // Sort by number of assigned locations, then by whether they have any
          const aCount = a.assignedLocationIds?.length || 0;
          const bCount = b.assignedLocationIds?.length || 0;
          comparison = aCount - bCount;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredUsers, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedUsers, currentPage]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole]);

  // Handle sort click
  const handleSort = (field: SortField) => {
    setCurrentPage(1);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort header component
  const SortHeader: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="text-gray-400">
          {sortField === field ? (
            sortDirection === 'asc' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )
          ) : (
            <svg className="w-4 h-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          )}
        </span>
      </div>
    </th>
  );

  // Get unique roles for filter
  const availableRoles = Array.from(new Set(users.map(u => u.role))).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
        <span className="ml-2 text-gray-500">Loading users...</span>
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="bg-red-50 rounded-lg p-6 text-center">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-800 text-lg">{error}</p>
        <button
          onClick={loadUsers}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">
            User Assignments
          </h3>
          <span className="text-sm text-gray-500">
            ({sortedUsers.length} user{sortedUsers.length !== 1 ? 's' : ''})
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <label className="text-sm text-gray-600">Filter by role:</label>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
          >
            <option value="all">All Roles</option>
            {availableRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
          <button
            onClick={loadUsers}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Info banner for DeptMgr */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 text-sm">
          <strong>Note:</strong> Location assignments affect <strong>Department Manager</strong> and
          <strong> F&amp;I Manager</strong> users; department assignments affect Department Managers only.
          A Department Manager sees their assigned location(s) and department(s); an F&amp;I Manager sees
          the full Nut Sheet for their assigned location(s) and <strong>nothing until a location is assigned</strong>.
          Other roles have access to all locations and departments.
        </p>
      </div>

      {/* Users list */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortHeader field="displayName" label="User" />
              <SortHeader field="role" label="Role" />
              <SortHeader field="assignedLocations" label="Locations" />
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Departments
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedUsers.map((user) => {
              const isEditing = editingUser?.userId === user.userId;
              const isDeptMgr = user.role === 'DepartmentManager';
              const isFinanceManager = user.role === 'FinanceManager';
              // Roles whose Nut Sheet access is scoped to assigned location(s).
              // DepartmentManager also takes a department assignment; FinanceManager does not.
              const isLocationScoped = isDeptMgr || isFinanceManager;

              return (
                <tr key={user.userId} className={isEditing ? 'bg-yellow-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <UserCircleIcon className="h-10 w-10 text-gray-400" />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {user.displayName}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeClass(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <div className="flex flex-wrap gap-2">
                        {LOCATIONS.map(loc => {
                          const isSelected = editingUser.locationIds.includes(loc.id);
                          return (
                            <button
                              key={loc.id}
                              onClick={() => toggleLocation(loc.id)}
                              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                isSelected
                                  ? 'bg-blue-100 border-blue-300 text-blue-800'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {isSelected && <CheckIcon className="h-3 w-3 inline mr-1" />}
                              {loc.name.replace("Darling's ", "").replace("Darlings ", "")}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {!isLocationScoped ? (
                          <span className="text-sm text-gray-500 italic">
                            All (role-based)
                          </span>
                        ) : user.assignedLocationIds?.length > 0 ? (
                          user.assignedLocationIds.map(locId => (
                            <span
                              key={locId}
                              className="inline-flex items-center px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full"
                            >
                              <MapPinIcon className="h-3 w-3 mr-1" />
                              {getLocationName(locId).replace("Darling's ", "").replace("Darlings ", "")}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-amber-600 italic">
                            {isFinanceManager ? 'None (sees nothing until assigned)' : 'None (sees all)'}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing && isDeptMgr ? (
                      <div className="flex flex-wrap gap-2">
                        {ASSIGNABLE_DEPARTMENTS.map(dept => {
                          const isSelected = editingUser.departmentIds.includes(dept.key);
                          return (
                            <button
                              key={dept.key}
                              onClick={() => toggleDepartment(dept.key)}
                              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                isSelected
                                  ? 'bg-indigo-100 border-indigo-300 text-indigo-800'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {isSelected && <CheckIcon className="h-3 w-3 inline mr-1" />}
                              {dept.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {isFinanceManager ? (
                          <span className="text-sm text-gray-500 italic">
                            N/A (location-scoped)
                          </span>
                        ) : !isDeptMgr ? (
                          <span className="text-sm text-gray-500 italic">
                            All (role-based)
                          </span>
                        ) : user.assignedDepartmentIds?.length ? (
                          user.assignedDepartmentIds.map(deptId => {
                            const dept = ASSIGNABLE_DEPARTMENTS.find(d => d.key === deptId);
                            return (
                              <span
                                key={deptId}
                                className="inline-flex items-center px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                              >
                                <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                                {dept?.label || deptId}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-sm text-amber-600 italic">
                            None (sees all)
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    {isEditing ? (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={saveAssignment}
                          disabled={saving}
                          className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {saving ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckIcon className="h-4 w-4" />
                          )}
                          <span className="ml-1">Save</span>
                        </button>
                        <button
                          onClick={cancelEditing}
                          disabled={saving}
                          className="inline-flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                          <span className="ml-1">Cancel</span>
                        </button>
                      </div>
                    ) : (
                      canEdit && isLocationScoped && (
                        <button
                          onClick={() => startEditing(user)}
                          className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
                        >
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          Edit Assignments
                        </button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {sortedUsers.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            No users found{filterRole !== 'all' ? ` with role "${filterRole}"` : ''}.
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {sortedUsers.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedUsers.length)} of {sortedUsers.length} users
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {(() => {
                  const pages: (number | string)[] = [];
                  const maxVisible = 5;

                  if (totalPages <= maxVisible) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                  } else {
                    if (currentPage <= 3) {
                      pages.push(1, 2, 3, 4, '...', totalPages);
                    } else if (currentPage >= totalPages - 2) {
                      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                    } else {
                      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                    }
                  }

                  return pages.map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-400">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page as number)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ));
                })()}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Help text */}
      <div className="text-sm text-gray-500">
        <p>
          <strong>How it works:</strong> Users appear here after they log in for the first time.
          Their role is determined by Azure AD group membership. Location and department assignments
          are stored separately and affect Department Manager and F&amp;I Manager access. Unassigned
          Department Managers see all locations and departments until configured; unassigned F&amp;I
          Managers see no Nut Sheet data until a location is assigned.
        </p>
      </div>
    </div>
  );
};
